import {useEffect, useRef, useState} from 'react'
import {
	getLevelProgressionFormattedData,
	getSynchronizedTime,
	initializeRegEndTime,
	parseDurationList,
	resetGameState,
	updateInfoData,
	updateTimeOffset,
} from 'shared/lib/gameLogic'

const useGameLogic = (eventData, durationList, setInfoData, idx, timeData) => {
	const [formattedGameData, setFormattedGameData] = useState(null)
	const prevEventStateRef = useRef(null) // Track previous event state for pause/resume detection
	const timeDataRef = useRef(timeData) // Ref to hold timeData without causing re-renders

	// Keep ref in sync with prop
	useEffect(() => {
		timeDataRef.current = timeData
	}, [timeData])

	const gameStateRef = useRef({
		durationIndex: -1,
		totalMinute: 0,
		startTime: null,
		regEndMinute: 0,
		nextBreakMinute: 0,
		isEarlyEnd: false,
		isRegEnd: false,
	})

	// Track tab visibility transitions. When a timer tab is hidden, Chrome
	// throttles the 1s setInterval; on visible, the next ticks see a big jump
	// in elapsedSeconds and processLevelUp advances level(s) that already
	// passed while hidden. We record the timestamp of the hidden->visible
	// transition so processLevelUp can suppress the sound on those catch-up
	// advances (within a short window) instead of replaying the level-up sound.
	useEffect(() => {
		const onVisibilityChange = () => {
			if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
				gameStateRef.current.justVisibleAt = Date.now()
			}
		}
		if (typeof document !== 'undefined') {
			document.addEventListener('visibilitychange', onVisibilityChange)
			return () => document.removeEventListener('visibilitychange', onVisibilityChange)
		}
	}, [])

	// Initial setup and data processing
	useEffect(() => {
		if (!eventData || !durationList) {
			console.log('🚀~ useGameLogic: Missing data')
			return
		}

		console.log('🚀~ useGameLogic: Initial setup')
		const gameState = gameStateRef.current

		// Detect pause/resume transitions for break time recalculation
		const previousEventState = prevEventStateRef.current
		const currentEventState = eventData.event_state

		if (previousEventState === 'pause' && currentEventState === 'taking') {
			console.log('🚀~ Detected resume from pause - recalculating break times')
			// Force break time recalculation after resume by clearing stored value
			gameState.nextBreakMinute = 0
		}

		// Update previous state for next comparison
		prevEventStateRef.current = currentEventState

		// Update time offset whenever we receive server time
		if (eventData.server_time) {
			updateTimeOffset(eventData.server_time)
		}

		// Update start time whenever it changes (handles admin resets)
		const newStartTime = new Date(eventData.start_time).getTime()
		if (gameState.startTime !== newStartTime) {
			console.log('🚀~ Start time changed! Resetting game state')
			gameState.startTime = newStartTime
			resetGameState(gameState, eventData)
		}

		// Initialize/update registration end minute (recalculate if reg_end_level changes)
		if (eventData.reg_end_level) {
			const parsedDurationList = parseDurationList(durationList)
			const calculatedRegEndMinute = initializeRegEndTime(
				parsedDurationList,
				eventData.reg_end_level
			)
			if (gameState.regEndMinute !== calculatedRegEndMinute) {
				gameState.regEndMinute = calculatedRegEndMinute
				console.log('🚀~ Updated reg end minute:', gameState.regEndMinute)
			}
		}

		// Initialize/update next break time using the same logic as calculateNextBreakTime
		const parsedDurationList = parseDurationList(durationList)

		// Find the next break in the duration list
		let nextBreakIndex = -1
		for (
			let i = gameState.durationIndex + 1;
			i < parsedDurationList.length;
			i++
		) {
			if (parsedDurationList[i][0] === 'break') {
				nextBreakIndex = i
				break
			}
		}

		if (nextBreakIndex !== -1) {
			// Calculate time from CURRENT position until the next break (consistent with calculateNextBreakTime)
			try {
				// Edge case: validate startTime before calculation
				if (!gameState.startTime || isNaN(gameState.startTime)) {
					console.warn(
						'🚀~ Invalid startTime in gameState, skipping break time calculation'
					)
					gameState.nextBreakMinute = 0
					return
				}

				const elapsedSeconds = Math.floor(
					(getSynchronizedTime() - gameState.startTime) / 1000
				)

				// Edge case: validate elapsed time is reasonable
				if (elapsedSeconds < 0) {
					console.warn('🚀~ Negative elapsed time detected:', elapsedSeconds)
					gameState.nextBreakMinute = 0
					return
				}

				const remainingCurrentLevelSeconds = Math.max(
					0,
					gameState.totalMinute - elapsedSeconds
				)
				let betweenLevelsSeconds = 0

				for (let i = gameState.durationIndex + 1; i < nextBreakIndex; i++) {
					// Edge case: validate duration entry and parse safely
					if (
						!Array.isArray(parsedDurationList[i]) ||
						parsedDurationList[i].length < 2
					) {
						console.warn('🚀~ Skipping malformed duration entry at index:', i)
						continue
					}

					const durationMinutes = parseInt(parsedDurationList[i][1])
					if (isNaN(durationMinutes) || durationMinutes < 0) {
						console.warn(
							'🚀~ Invalid duration minutes at index:',
							i,
							parsedDurationList[i][1]
						)
						continue
					}

					betweenLevelsSeconds += durationMinutes * 60
				}

				const timeToNextBreakSeconds =
					remainingCurrentLevelSeconds + betweenLevelsSeconds

				// Edge case: ensure we don't set negative or invalid times
				if (timeToNextBreakSeconds < 0) {
					console.warn(
						'🚀~ Calculated negative break time in initialization, setting to 0'
					)
					gameState.nextBreakMinute = elapsedSeconds // Will result in 0 remaining time
				} else {
					// Store as absolute moment (so remaining = nextBreakMinute - elapsedSeconds)
					const calculatedNextBreakMinute =
						elapsedSeconds + timeToNextBreakSeconds
					if (gameState.nextBreakMinute !== calculatedNextBreakMinute) {
						gameState.nextBreakMinute = calculatedNextBreakMinute
					}
				}
			} catch (error) {
				console.error(
					'🚀~ Error calculating break time in initialization:',
					error
				)
				gameState.nextBreakMinute = 0
			}
		} else {
			// No break found
			gameState.nextBreakMinute = 0
		}

		// Handle pause state
		if (eventData.event_state === 'pause') {
			const parsedDurationList = parseDurationList(durationList)
			const formattedData = getLevelProgressionFormattedData(
				parsedDurationList,
				gameState,
				eventData,
				idx
			)
			const dataWithPause = {
				...formattedData,
				pause: true,
			}
			setFormattedGameData(dataWithPause)
			// Also update infoData to show correct values when paused
			updateInfoData(
				dataWithPause,
				setInfoData,
				parsedDurationList,
				gameState,
				timeDataRef.current
			)
			return
		}
	}, [eventData, durationList])

	// Level progression timer - runs every second
	useEffect(() => {
		if (!eventData || !durationList || eventData.event_state !== 'taking') {
			return
		}

		const checkLevelProgression = () => {
			try {
				const gameState = gameStateRef.current

				if (!gameState.startTime) {
					console.log('🚀~ No start time set yet')
					return
				}

				const parsedDurationList = parseDurationList(durationList)
				const formattedData = getLevelProgressionFormattedData(
					parsedDurationList,
					gameState,
					eventData,
					idx
				)

				setFormattedGameData(formattedData)
				updateInfoData(
					formattedData,
					setInfoData,
					parsedDurationList,
					gameState,
					timeDataRef.current
				)
			} catch (error) {
				console.error('🚀~ Error in level progression:', error)
			}
		}

		// Run immediately
		checkLevelProgression()

		// Set up interval to check every second
		const interval = setInterval(checkLevelProgression, 1000)

		return () => clearInterval(interval)
	}, [eventData, durationList, setInfoData, idx]) // Removed timeData to prevent circular updates

	return {
		formattedGameData,
		gameState: gameStateRef.current,
	}
}

export default useGameLogic